import React, {Fragment} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {useHistory, resolveRoute} from '@computerrock/formation-router';
import {useTranslate} from '@computerrock/formation-i18n';
import {ButtonPrimary, ButtonSecondary, ContentBlock, ContentItem, Icon, phoneIcon, useStyles, warningOutlinedIcon, emailIcon, checkmarkIcon, Card, Circle} from '@ace-de/ui-components';
import ContactCard from '../application/view-elements/ContactCard';
import ContactDetailRow from '../application/view-elements/ContactDetailRow';
import routePaths from '../routePaths';
import {createInvoiceSubmissionStatuses} from './constants/createInvoiceSubmissionStatuses';
import * as invoiceSubmissionActionTypes from './invoiceSubmissionActionTypes';

const statusIcon = {
    [createInvoiceSubmissionStatuses.SUCCEEDED]: checkmarkIcon,
    [createInvoiceSubmissionStatuses.FAILED]: warningOutlinedIcon,
    [createInvoiceSubmissionStatuses.FAILED_AGAIN]: warningOutlinedIcon,
};

const CreateStatusScreen = props => {
    const {cx} = useStyles();
    const history = useHistory();
    const {createTranslateShorthand} = useTranslate();
    const translateScreen = createTranslateShorthand('create_status_screen');
    const {status, reSubmitForm, changeCreateStatus, resubmitCount, resetResubmitCount} = props;
    const {storeInvoiceSubmissionDraft} = props;

    return (
        <Fragment>
            <ContentBlock
                className={cx([
                    'global!ace-u-flex',
                    'global!ace-u-flex--align-center',
                    'global!ace-u-flex--justify-center',
                ])}
            >
                <ContentItem
                    className={cx([
                        'ace-c-content-item--base-span-12',
                        'ace-c-content-item--small-span-8',
                        'ace-c-content-item--large-span-6',
                        'ace-c-content-item--x-large-span-4',

                    ])}
                >
                    <Card
                        className={cx([
                            'global!ace-u-padding--small-16',
                            'global!ace-u-padding--x-large-32',
                            'global!ace-u-flex--align-center',
                        ])}
                    >
                        <Circle
                            className={cx('ace-c-circle--medium-responsive', 'global!ace-u-margin--small-bottom-32', {
                                'ace-c-circle--is-success': status === createInvoiceSubmissionStatuses.SUCCEEDED,
                                'ace-c-circle--is-warning': status === createInvoiceSubmissionStatuses.FAILED || status === createInvoiceSubmissionStatuses.FAILED_AGAIN,
                            })}
                        >
                            <Icon
                                icon={statusIcon[status]}
                                className={cx('ace-c-icon--xl', {
                                    'ace-c-icon--color-success': status === createInvoiceSubmissionStatuses.SUCCEEDED,
                                    'ace-c-icon--color-warning-dark': status === createInvoiceSubmissionStatuses.FAILED || status === createInvoiceSubmissionStatuses.FAILED_AGAIN,
                                })}
                            />
                        </Circle>
                        <h2
                            className={cx([
                                'global!ace-u-typography--variant-h2',
                                'global!ace-u-margin--small-bottom-24',
                                'global!ace-u-typography--align-center',
                            ])}
                        >
                            {status === createInvoiceSubmissionStatuses.FAILED_AGAIN ? `${translateScreen(`title.${status.toLowerCase()}`)} ${resubmitCount}` : translateScreen(`title.${status.toLowerCase()}`)}
                        </h2>
                        <p
                            className={cx([
                                'global!ace-u-typography--variant-body',
                                'global!ace-u-typography--align-center',
                                'global!ace-u-margin--small-bottom-24',
                                'global!ace-u-margin--x-large-bottom-48',
                            ])}
                        >
                            {translateScreen(`text.${status.toLowerCase()}`)}
                        </p>
                        <div className={cx([
                            'global!ace-u-width--small-full',
                            'global!ace-u-flex',
                            'global!ace-u-flex--justify-center',
                            'global!ace-u-flex--small-direction-column',
                            'global!ace-u-flex--x-large-direction-row',
                        ])}
                        >
                            {(status === createInvoiceSubmissionStatuses.FAILED
                                    || status === createInvoiceSubmissionStatuses.FAILED_AGAIN)
                                && (
                                    <ButtonPrimary
                                        className={cx([
                                            'global!ace-u-width--small-full',
                                            'global!ace-u-width--large-auto',
                                            'global!ace-u-margin--small-bottom-24',
                                            'global!ace-u-margin--x-large-right-24',
                                        ])}
                                        onClick={reSubmitForm}
                                    >
                                        {translateScreen('button_label.try_again')}
                                    </ButtonPrimary>
                                )}
                            <ButtonSecondary
                                className={cx(['global!ace-u-width--small-full', 'global!ace-u-width--large-auto'])}
                                onClick={() => {
                                    changeCreateStatus({createInvoiceSubmissionState: createInvoiceSubmissionStatuses.IDLE}); // eslint-disable-line
                                    resetResubmitCount();
                                    storeInvoiceSubmissionDraft({invoiceSubmissionDraft: null});
                                    history.push(resolveRoute(routePaths.DASHBOARD));
                                }}
                            >
                                {translateScreen('button_label.to_home_page')}
                            </ButtonSecondary>
                        </div>
                    </Card>
                </ContentItem>
            </ContentBlock>
            <ContentBlock className={cx([
                'global!ace-u-position--small-absolute',
                'global!ace-u-position--small-bottom-0',
                'global!ace-u-position--small-left-8',
                'global!ace-u-flex',
                'global!ace-u-flex--justify-center',
                'global!ace-u-width--full',
                'global!ace-u-padding--small-left-16',
                'global!ace-u-padding--small-right-16',
                'global!ace-u-padding--x-large-left-64',
                'global!ace-u-margin--x-large-left-64',
                'global!ace-u-padding--x-large-right-64',
                'global!ace-u-margin--x-large-right-64',
            ])}
            >
                <ContentItem>
                    <ContactCard>
                        <h3 className={cx([
                            'global!ace-u-typography--variant-body-bold',
                            'global!ace-u-margin--small-bottom-8',
                            'global!ace-u-margin--x-large-bottom-0',
                            'global!ace-u-margin--x-large-right-48',
                        ])}
                        >
                            Kontakt zum Mitglied-Service
                        </h3>
                        <ContactDetailRow>
                            <Icon className={cx(['ace-c-icon--m', 'ace-c-icon--color-highlight', 'global!ace-u-margin--right-8'])} icon={emailIcon} />
                            <a href="mailto:info@ace.de" className={cx(['global!ace-u-typography--variant-body-medium'])}>info@ace.de</a>
                        </ContactDetailRow>
                        <div className={cx(['global!ace-u-margin--bottom-16', 'global!ace-u-margin--x-large-right-48'])} />
                        <ContactDetailRow>
                            <Icon className={cx(['ace-c-icon--m', 'ace-c-icon--color-highlight', 'global!ace-u-margin--right-8'])} icon={phoneIcon} />
                            <a href="tel:+49711530336677" className={cx(['global!ace-u-typography--variant-body-medium'])}>+49 711 530 33 66 77</a>
                        </ContactDetailRow>
                    </ContactCard>
                </ContentItem>
            </ContentBlock>
        </Fragment>

    );
};

CreateStatusScreen.propTypes = {
    status: PropTypes.string.isRequired,
    reSubmitForm: PropTypes.func.isRequired,
    changeCreateStatus: PropTypes.func.isRequired,
    resetResubmitCount: PropTypes.func.isRequired,
    resubmitCount: PropTypes.number.isRequired,
    storeInvoiceSubmissionDraft: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
    storeInvoiceSubmissionDraft: payload => dispatch({
        type: invoiceSubmissionActionTypes.STORE_INVOICE_SUBMISSION_DRAFT,
        payload,
    }),
});


export default connect(null, mapDispatchToProps)(CreateStatusScreen);
